/**
 * Simplified Chinese message catalog — mirrors the shape of
 * `messages.en.ts` exactly; `satisfies MessagesShape` makes a missing or
 * renamed key a TypeScript error. Register: 你 (no T–V distinction
 * locked in l10n/locales.md; modern app convention, never 您).
 *
 * Platform terms follow zh-CN UI conventions (设置, 配对 for pairing,
 * 镜像 for mirroring — Android-zh-CN's own screen-mirroring term,
 * 通知使用权 for notification access, 清除 for dismiss). "Glance" is
 * the untranslated feature brand name (glossary D1); "剪贴项" renders
 * the D2 clip noun (proposed zh reading — see the session report).
 *
 * Plural notes: Chinese is an `other`-only CLDR locale — measure words
 * (条/台/个) carry the number; every `selectPlural("zh-CN", …)`
 * supplies only an `other` form.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "已连接",
    statusReconnecting: "正在重新连接…",
    statusDisconnected: "已断开",
    openSettings: "打开设置",
    pairedFallbackDevice: "你的手机",
    publicModeToggleEnable: "隐藏通知内容",
    publicModeToggleDisable: "显示通知内容",
  },

  notifications: {
    replyAriaLabel: "回复",
    replyPlaceholder: "回复…",
    replySend: "发送",
    sent: "已发送",
    failedToSend: "发送失败",
    unread: "未读",
    dismiss: "清除",
    notificationImageAlt: "通知图片",
  },

  conversations: {
    replyAriaLabel: "回复",
    replyPlaceholder: "回复…",
    replySend: "发送",
    sent: "已发送",
    failedToSend: "发送失败",
    dismiss: "清除对话",
    messagesListLabel: "此对话中的消息",
    deviceOwner: "我",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "没有未读消息"
        : `${count} ${selectPlural("zh-CN", count, { other: "条未读消息" })}`,
  },

  list: {
    today: "今天",
    yesterday: "昨天",
    searchNotifications: "搜索通知",
    clearSearch: "清除搜索",
    clearAll: "全部清除",
    markAllRead: "全部标为已读",
    groupCheckboxLabel: "分组显示",
    noNotificationsTitle: "还没有通知",
    noNotificationsDesc: "从手机镜像过来的新消息会显示在这里。",
    noMatchesTitle: "没有匹配结果",
    notificationsList: "通知",
    privacyAppList: "按应用查看通知",
    clear: "清除",
    clearMatches: (count: number): string =>
      `清除 ${count} ${selectPlural("zh-CN", count, { other: "条匹配结果" })}`,
    noMatchesDesc: (query: string): string => `没有与“${query}”匹配的通知。`,
    appNotificationsGroup: (appName: string): string => `${appName} 的通知`,
    dismissAllFrom: (appName: string): string => `清除 ${appName} 的全部通知`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("zh-CN", count, { other: "条消息" })
          : kind === "calls"
            ? selectPlural("zh-CN", count, { other: "个来电" })
            : selectPlural("zh-CN", count, { other: "条通知" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "清除",
    kbdNavigate: "切换",
    kbdOrSeparator: "或",
  },

  settings: {
    back: "返回通知列表",
    settings: "设置",
    general: "常规",
    theme: "主题",
    themeAuto: "自动",
    themeLight: "浅色",
    themeDark: "深色",
    language: "语言",
    languageSystemDefault: "系统默认",
    notificationsSection: "通知",
    osNotifications: "系统通知",
    callNotifications: "来电通知",
    privacySecurity: "隐私",
    analyticsToggle: "匿名使用统计",
    analyticsToggleDescription: "仅计数和里程碑——绝不包含你的通知。",
    statusConnected: "已连接",
    statusReconnecting: "正在重新连接…",
    statusDisconnected: "已断开",
    disconnectFromPhone: "与手机断开连接",
    disconnectConfirm: "确定要断开吗？设备将解除配对。",
    yesDisconnect: "确定断开",
    cancel: "取消",
    version: "版本",
    encryption: "加密",
    encryptionValue: "E2E (NaCl)",
    needHelp: "需要帮助？发邮件到 __EMAIL__",
    disconnectFailed: "断开失败——请重试",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `已断开，但本地清理失败：${errorMsg}。请刷新页面。`,
    disconnectLocalCleanupFailed: "已断开，但本地清理失败。请刷新页面。",
  },

  pairing: {
    enterCodeInstruction: "在 Reflecto 应用中输入此配对码",
    pairedSuccessfully: "配对成功！",
    pairingFailed: "配对失败",
    requestingPairingCode: "正在请求配对码…",
    codeExpired: "配对码已过期",
    tryAgain: "再试一次",
    failedToStartPairing: "无法开始配对",
    installHeading: "还没有安装应用？",
    installScanHint: "用手机相机扫描",
    installQrLabel: "用于在手机上安装 Reflecto 的二维码",
    capabilitiesHeading: "配对后你可以：",
    capabilityMirror: "在这里查看手机通知",
    capabilityReply: "无需拿起手机即可回复",
    capabilityShare: "双向共享剪贴板和文件",
    encryptionNote: "端到端加密，服务器永远看不到你的内容。",
  },

  dnd: {
    title: "勿扰",
    description: "在接下来的 1 小时内暂停此设备上的通知。",
    snooze1h: "暂停 1 小时",
    snoozeActiveUntil: (formattedTime: string): string => `生效至 ${formattedTime}`,
    cancelSnooze: "取消",
  },

  connectors: {
    title: "已连接的服务",
    emptyBody:
      "还没有已连接的服务。在 ChatGPT 或 Claude 的连接器设置中发起连接，然后在手机上批准配对码。",
    scopeSend: "可以发送通知",
    added: (date: string): string => `添加于 ${date}`,
    lastUsed: (relative: string): string => `上次使用 ${relative}`,
    neverUsed: "从未使用",
    revoke: "断开",
    revokeConfirm: (label: string): string => `要断开 ${label} 吗？它会立即失去访问权限。`,
    revokeAction: "断开",
    revokeConfirmLabel: (label: string): string => `断开 ${label}`,
    cancel: "取消",
    loadError: "无法加载已连接的服务。请重试。",
    revokeError: "无法断开该服务。请重试。",
    retry: "重试",
  },

  safetyNumber: {
    rowLabel: "验证加密",
    rowSublabel: "与您的手机比较安全码",
    cardTitle: "安全码",
    caption: (deviceLabel: string): string =>
      `与 ${deviceLabel} 上显示的号码进行比对。如果两者都匹配，说明您的端到端加密完整。`,
    repairNote: "重新配对设备时，此号码会改变。",
    copy: "复制",
    copied: "已复制",
    maskedHint: "隐私模式启用时隐藏",
  },

  feedback: {
    title: "发送反馈",
    close: "关闭",
    messageLabel: "反馈内容",
    messagePlaceholder: "发现了 Bug、有想法，或者觉得哪里不对劲？告诉我们。",
    categoryLabel: "类别",
    categoryBug: "Bug",
    categoryIdea: "想法",
    categoryOther: "其他",
    emailLabel: "电子邮件（选填，仅在需要回复时填写）",
    emailPlaceholder: "you@example.com",
    emailHelp: "我们不会把你加入任何邮件列表。",
    disclosure:
      "你的反馈会发送到 Reflecto 服务器，并记录在我们的内部问题跟踪系统中。为了关联报告，我们会附上应用版本和配对时分配的设备 ID。我们看不到你的通知、联系人或任何个人数据。电子邮件为选填——仅在需要回复时填写。我们不会把你加入任何邮件列表。",
    submit: "发送反馈",
    sending: "正在发送…",
    success: "谢谢——会有真人阅读，通常在 24 小时内",
    error: "发送失败。请重试。",
    done: "完成",
    sendFeedback: "发送反馈",
  },

  uninstall: {
    headline: "Reflecto 扩展程序已卸载",
    body: "从来就没有账号，扩展程序也只把配对信息保存在这台电脑上，所以没有什么需要你清理的。",
    ask: "如果你有三十秒，我们想知道哪里出了问题。",
    categoryLegend: "这是哪一类反馈？",
    categoryBug: "有功能坏了",
    categoryIdea: "缺少我需要的功能",
    categoryOther: "其他情况",
    messageLabel: "哪里出了问题？",
    messagePlaceholder: "过了几个小时后就收不到通知了。",
    messageRequired: "先写一句话再发送。",
    charactersLeft: (count: number): string => `还剩 ${count} 个字符`,
    emailLabel: "电子邮件（选填）",
    emailPlaceholder: "you@example.com",
    emailHelp: "仅用于回复你，不作他用。",
    submit: "发送反馈",
    sending: "正在发送…",
    retry: "再试一次",
    privacyNote:
      "此表单不与任何设备关联，不包含你的通知、剪贴板或文件。电子邮件为选填，仅用于回复。",
    success: "谢谢。每一条都会有真人阅读。",
    errorGeneric: "没有发送成功。你写的内容还在。",
    errorRateLimited: "你最近发送了多条消息。请大约一小时后再试。你写的内容会保留在这里。",
    footerWebApp: "没有扩展程序，Reflecto 网页应用仍然可用。",
    footerWebAppLink: "打开网页应用",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "关闭",
    aiSummaryTag: "AI 摘要",
    errorAi: "现在无法生成摘要。",
    retryCta: "再试一次",
    downloadHeading: "获取 AI 摘要",
    downloadBody: "用设备端 AI 摘要快速了解错过的通知。全部在你的设备上运行。",
    downloadCta: "下载模型",
    downloadPrivacyNote: "只需下载一次。服务器永远看不到你的通知。",
    downloadingHeading: "正在下载模型…",
    caughtUp: "都看完了——现在没有需要你处理的事。",
    needsYou: "需要处理",
    quiet: "其他",
    scanning: (pending: number): string => `正在摘要 ${pending} 条…`,
    jump: "打开通知",
    affordanceReply: "回复",
    affordanceOpen: "打开",
    affordanceCopyOtp: "复制验证码",
    buttonLabel: "Glance",
  },

  update: {
    title: "有可用更新",
    description: "新版 Reflecto 已就绪。刷新即可更新。",
    reload: "刷新",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `我们将这台设备命名为“${name}”。`,
    cardLooksGood: "可以",
    cardEdit: "修改",
    cardAriaLabel: "确认设备名称",
    renameTitle: "重命名这台设备",
    renameLabel: "设备名称",
    renameSave: "保存",
    renameCancel: "取消",
    renameFailed: "重命名失败。请重试。",
  },

  collision: {
    title: (name: string): string => `你已经有一台“${name}”`,
    subtitle: "这台设备叫什么好？",
    chipWork: (name: string): string => `工作 ${name}`,
    chipHome: (name: string): string => `家用 ${name}`,
    chipPersonal: (name: string): string => `个人 ${name}`,
    customLabel: "自定义名称",
    save: "保存",
    useAnyway: "仍使用原来的名称",
    saveFailed: "新名称保存失败。请重试。",
    ariaLabel: "解决重复的设备名称",
  },

  clipboard: {
    title: "剪贴板",
    emptyTitle: "还没有剪贴项",
    empty: "其他设备上的剪贴项会显示在这里。点按即可复制。",
    from: (label: string): string => `来自 ${label}`,
    to: (label: string): string => `发往 ${label}`,
    copy: "复制到剪贴板",
    copied: "已复制",
    reveal: "显示",
    hide: "隐藏",
    sensitiveChip: "敏感内容",
    cloudChip: "来自你的 API 令牌",
    maskedHint: "内容已隐藏——点按“显示”查看",
    delete: "删除剪贴项",
    sendPlaceholder: "粘贴要发送的文本…",
    sendCta: "发送",
    sendSensitiveToggle: "标记为敏感内容",
    sendSuccess: (n: number): string =>
      `已发送到 ${n} ${selectPlural("zh-CN", n, { other: "台设备" })}。`,
    sendPartial: (n: number, total: number): string =>
      `已发送到 ${total} ${selectPlural("zh-CN", total, { other: "台设备" })}中的 ${n} 台。`,
    sendNoDevices: "没有可接收的已配对设备。",
    sendFailed: "发送失败。请重试。",
    sendEmpty: "没有可发送的内容。",
    dockRecentHeading: "最近",
    dockEmptyClipboard: "剪贴板是空的。",
    clearAll: "全部清除",
    clearAllConfirm: "确定清除全部",
    clearAllCancel: "取消",
    shareTitle: "发送到我的设备",
    shareReview: "确认要发送的内容。它会经端到端加密发送到你的已配对设备。",
    shareSend: "发送",
    shareCancel: "取消",
    shareNotPaired: "要发送分享的文本，请先配对一台设备。",
    shareOpenApp: "打开 Reflecto",
    pasteFromClipboard: "从剪贴板粘贴",
    readFailed: "无法读取剪贴板，请在下方粘贴。",
  },
  install: {
    title: "安装 Reflecto",
    body: "添加到主屏幕，访问更快捷。",
    cta: "安装",
    dismissAria: "关闭安装提示",
  },
  nav: {
    home: "主页",
    clipboard: "剪贴板",
    files: "文件",
    ariaLabel: "主导航",
    fileHiddenPublicMode: "隐私模式开启时隐藏。",
  },
  common: {
    loading: "正在加载",
  },
  errors: {
    notPaired: "未配对",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${os} 上的 ${browser}`,
    webFallback: "网页浏览器",
  },
  palette: {
    placeholder: "输入命令…",
    noResults: "没有匹配的命令",
    ariaLabel: "命令面板",
    clearAll: "清除所有通知",
    glance: "Glance 摘要",
    cancelSnooze: "取消暂停",
    copyClip: "复制最近收到的剪贴项",
  },

  fileTransfer: {
    sendTitle: "发送文件",
    sendChooseFile: "选择文件",
    sendDeviceLabel: "发送到",
    sendPhoneFallbackName: "手机",
    send: "发送文件",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "正在准备…" : `正在准备——${pct}%`,
    sendDelivering: "正在发送到手机…",
    sendWaitingAccept: "正在等待手机接受…",
    sendWaitingAppOpen: "在手机上打开 Reflecto 以接受",
    sendWaitingAppOpenHint: "你的手机无法接收推送通知，因此只有打开 Reflecto 后才会显示此文件。",
    sendUploading: (partNumber: number, partCount: number): string =>
      `正在上传第 ${partNumber}/${partCount} 部分…`,
    sendDone: "已发送。",
    sendFailedRejected: "手机拒收了此文件。",
    sendFailedNoAnswer: "手机没有响应。",
    sendFailedUploadFailed: "上传失败。请重试。",
    sendFailedCancelled: "已取消。",
    sendFailedGeneric: "出了点问题。请重试。",
    sendTooLarge: (maxMiB: number): string => `文件过大（最大 ${maxMiB} MiB）。`,
    sendKeepOpenHint: "在传输完成前，请保持此标签页处于打开状态。",
    sendAnother: "再发一个",
    sendNoDevice: "要发送文件，请先配对一台手机。",
    sendCancel: "取消",
    sendPeerProgress: (chunksDone: number): string => `手机已接收 ${chunksDone} 个数据块。`,

    receiveTitle: "传入的文件",
    receiveOfferFrom: (sender: string): string => `${sender} 想向你发送一个文件`,
    receiveAccept: "接受",
    receiveDecline: "拒绝",
    receiveReceiving: (n: number, total: number): string => `正在接收第 ${n}/${total} 部分…`,
    receiveDone: "已保存。",
    receiveFailed: "传输失败。",
    receiveCancelled: "已取消。",
    receiveCancel: "取消",

    errorChunkAuthFail: "传输失败——无法验证数据。请重试。",
    errorHashMismatch: "传输失败——文件未通过完整性校验。请重试。",
    errorStorageFull: "存储空间不足，无法完成此次传输。",
    errorExpired: "传输已过期。",
    errorQuotaExceeded: "已达到每日传输上限——请明天再试。",
    errorInternal: "传输失败——出了点问题。",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct}%`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "关闭",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "传输进度",
    outgoing: (n: number): string => `发送中 (${n})`,
    incoming: (n: number): string => `接收中 (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "全部接受",
    dropTitle: "拖放到此处",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `最大 ${size} 的文件。`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "拖放到此处，或选择要发送的文件",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "直连",
    transportDirectHint: "设备间直接发送 — 从不存储在我们的服务器上。",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "经服务器",
    transportRelayHint: "经我们的服务器中转，仍为端到端加密 — 我们无法读取。",
    directBadge: "直连",
  },
  time: {
    justNow: "刚刚",
    minutesAgo: (count: number): string => `${count} 分钟前`,
    hoursAgo: (count: number): string => `${count} 小时前`,
  },
} satisfies MessagesShape;
